import type { SpecialEvent } from '../types';

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface GeocodeResult extends Coordinates {
  label: string;
}

const geocodeCache = new Map<string, GeocodeResult | null>();

export function normalizeLocationInput(value: string) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function cleanAddressPart(value: unknown) {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function includesAddressPart(address: string, part: string) {
  if (!address || !part) return false;
  return address.toLowerCase().includes(part.toLowerCase());
}

function uniqueLocations(values: string[]) {
  const seen = new Set<string>();
  return values
    .map((value) => normalizeLocationInput(value))
    .filter((value) => {
      if (!value) return false;
      const key = value.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export function venueAddress(event: Pick<SpecialEvent, 'address' | 'streetAddress' | 'city' | 'state' | 'zipCode' | 'venueName'>) {
  const street = cleanAddressPart(event.streetAddress || event.address);
  const city = cleanAddressPart(event.city);
  const state = cleanAddressPart(event.state);
  const zipCode = cleanAddressPart(event.zipCode);

  const parts = [street];

  if (city && !includesAddressPart(street, city)) parts.push(city);
  if (state && !includesAddressPart(street, state)) parts.push(state);
  if (zipCode && !includesAddressPart(street, zipCode)) parts.push(zipCode);

  const fullAddress = parts.filter(Boolean).join(', ');
  return fullAddress || cleanAddressPart(event.venueName);
}

export function venueLocationCandidates(event: Pick<SpecialEvent, 'address' | 'streetAddress' | 'city' | 'state' | 'zipCode' | 'venueName'>) {
  const city = cleanAddressPart(event.city);
  const state = cleanAddressPart(event.state);
  const zipCode = cleanAddressPart(event.zipCode);
  const venueName = cleanAddressPart(event.venueName);

  return uniqueLocations([
    venueAddress(event),
    [city, state, zipCode].filter(Boolean).join(', '),
    zipCode,
    [venueName, city, state].filter(Boolean).join(', '),
  ]);
}

export function hasUsableCoords(value: any): value is Coordinates {
  return (
    value &&
    typeof value.lat === 'number' &&
    typeof value.lng === 'number' &&
    Number.isFinite(value.lat) &&
    Number.isFinite(value.lng)
  );
}

export function haversineMiles(a: Coordinates, b: Coordinates) {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadiusMiles = 3958.7613;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.sin(dLng / 2) * Math.sin(dLng / 2) * Math.cos(lat1) * Math.cos(lat2);

  return 2 * earthRadiusMiles * Math.asin(Math.sqrt(h));
}

export async function geocodeLocation(rawLocation: string): Promise<GeocodeResult | null> {
  const location = normalizeLocationInput(rawLocation);
  if (!location) return null;

  const cacheKey = location.toLowerCase();
  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey) ?? null;
  }

  try {
    const res = await fetch('/.netlify/functions/geocode-location', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ location }),
    });

    if (!res.ok) {
      geocodeCache.set(cacheKey, null);
      return null;
    }

    const payload = await res.json().catch(() => null);
    const result = payload?.data;
    const lat = Number(result?.lat);
    const lng = Number(result?.lng);

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      geocodeCache.set(cacheKey, null);
      return null;
    }

    const geocoded = {
      lat,
      lng,
      label: String(result?.label || location),
    };

    geocodeCache.set(cacheKey, geocoded);
    return geocoded;
  } catch (error) {
    console.error('[location search] geocode failed:', error);
    geocodeCache.set(cacheKey, null);
    return null;
  }
}

export async function geocodeVenue(event: Pick<SpecialEvent, 'address' | 'streetAddress' | 'city' | 'state' | 'zipCode' | 'venueName'>) {
  for (const candidate of venueLocationCandidates(event)) {
    const result = await geocodeLocation(candidate);
    if (result) return result;
  }

  return null;
}

export function getBrowserLocation(): Promise<GeocodeResult> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Your browser does not support current-location search.'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          label: 'Current location',
        });
      },
      () => reject(new Error('We could not access your current location. You can still search by ZIP or city.')),
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  });
}
